import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Save, Send, Mail, Power, PowerOff } from "lucide-react";

export default function EmailTemplatesAdmin() {
  const [templates, setTemplates] = useState([]);
  const [draft, setDraft] = useState({});       // id -> patch
  const [savingId, setSavingId] = useState(null);
  const [testTo, setTestTo] = useState({});      // id -> email

  const load = async () => {
    try { const r = await api.get("/admin/lead-templates"); setTemplates(r.data); }
    catch { toast.error("Could not load templates"); }
  };
  useEffect(() => { load(); }, []);

  const upd = (id, key, value) => setDraft(d => ({ ...d, [id]: { ...(d[id] || {}), [key]: value } }));

  const save = async (t) => {
    const patch = draft[t.id];
    if (!patch || Object.keys(patch).length === 0) return;
    setSavingId(t.id);
    try {
      await api.put(`/admin/lead-templates/${t.id}`, patch);
      toast.success("Template saved");
      setDraft(d => { const next = { ...d }; delete next[t.id]; return next; });
      load();
    } catch { toast.error("Save failed"); }
    finally { setSavingId(null); }
  };

  const toggle = async (t) => {
    await api.put(`/admin/lead-templates/${t.id}`, { enabled: !t.enabled });
    load();
  };

  const sendTest = async (t) => {
    const to = (testTo[t.id] || "").trim();
    if (!to.includes("@")) { toast.error("Enter a valid test email address"); return; }
    try {
      const r = await api.post(`/admin/lead-templates/${t.id}/test-send`, { to, client_name: "Test Client" });
      if (r.data.ok) toast.success(`Test sent to ${to}`);
      else toast.error(`Test failed: ${r.data.error || "unknown"}`);
    } catch { toast.error("Test send failed"); }
  };

  const merge = (t) => ({ ...t, ...(draft[t.id] || {}) });

  return (
    <div className="space-y-4" data-testid="email-templates-admin">
      <div className="bg-[#0a0a0a] border border-white/10 p-5">
        <div className="flex items-start gap-3">
          <Mail className="text-[#D4AF37] mt-1" size={20}/>
          <div>
            <h3 className="font-serif text-xl">Status-change email automation</h3>
            <p className="text-sm text-white/60 mt-1 leading-relaxed">
              When you change a lead's status in the <strong>Leads</strong> tab, the matching template
              below is automatically emailed to that client. Disable any template you don't want sent.
            </p>
            <p className="text-xs text-white/40 mt-2">
              Available placeholders: <code className="text-[#D4AF37]">{`{{ClientName}}`}</code>, <code className="text-[#D4AF37]">{`{{FirstName}}`}</code>, <code className="text-[#D4AF37]">{`{{Email}}`}</code>, <code className="text-[#D4AF37]">{`{{Phone}}`}</code>, <code className="text-[#D4AF37]">{`{{WeddingDate}}`}</code>, <code className="text-[#D4AF37]">{`{{Venue}}`}</code>, <code className="text-[#D4AF37]">{`{{City}}`}</code>
            </p>
          </div>
        </div>
      </div>

      {templates.map(t => {
        const v = merge(t);
        const hasChanges = !!draft[t.id] && Object.keys(draft[t.id] || {}).length > 0;
        return (
          <div key={t.id} className={`bg-[#0a0a0a] border ${v.enabled ? "border-white/10" : "border-white/5 opacity-75"}`}
               data-testid={`template-${t.status_key}`}>
            <div className="flex items-center justify-between p-5 border-b border-white/5">
              <div>
                <div className="text-[0.6rem] tracking-[0.3em] uppercase text-white/40">When status changes to</div>
                <div className="flex items-center gap-3 mt-1">
                  <span className="font-serif text-2xl text-[#D4AF37]">{t.label}</span>
                  <span className={`text-[0.55rem] tracking-widest uppercase px-2 py-0.5 ${v.enabled ? "bg-green-500/20 text-green-300" : "bg-white/10 text-white/40"}`}>
                    {v.enabled ? "Active" : "Disabled"}
                  </span>
                </div>
              </div>
              <button onClick={() => toggle(t)}
                className={`px-3 py-2 text-xs tracking-widest uppercase border ${v.enabled ? "border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10" : "border-white/15 text-white/50 hover:border-white/30"}`}
                data-testid={`toggle-${t.status_key}`}>
                {v.enabled ? <><PowerOff size={12} className="inline mr-1"/> Disable</> : <><Power size={12} className="inline mr-1"/> Enable</>}
              </button>
            </div>

            <div className="p-5 space-y-4">
              <label className="block">
                <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">Subject Line</span>
                <input value={v.subject || ""} onChange={e => upd(t.id, "subject", e.target.value)}
                  data-testid={`subject-${t.status_key}`}
                  className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none" />
              </label>
              <label className="block">
                <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">Message Body</span>
                <textarea value={v.body || ""} onChange={e => upd(t.id, "body", e.target.value)}
                  rows={8} data-testid={`body-${t.status_key}`}
                  className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm leading-relaxed focus:border-[#D4AF37] outline-none" />
              </label>

              <div className="flex flex-wrap items-center gap-3">
                <button onClick={() => save(t)} disabled={!hasChanges || savingId === t.id}
                  className="btn-gold disabled:opacity-40" data-testid={`save-${t.status_key}`}>
                  <Save size={14}/> {savingId === t.id ? "Saving..." : "Save Changes"}
                </button>
                <div className="flex items-center gap-2 flex-1 min-w-[280px]">
                  <input type="email" placeholder="your@email.com to receive a test"
                    value={testTo[t.id] || ""} onChange={e => setTestTo(s => ({ ...s, [t.id]: e.target.value }))}
                    data-testid={`test-to-${t.status_key}`}
                    className="flex-1 bg-black border border-white/15 px-3 py-2 text-xs focus:border-[#D4AF37] outline-none" />
                  <button onClick={() => sendTest(t)} className="btn-ghost text-xs whitespace-nowrap"
                    data-testid={`send-test-${t.status_key}`}>
                    <Send size={12}/> Send Test
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------- Per-lead email history modal ---------- */
export function LeadEmailHistoryModal({ leadId, leadName, onClose }) {
  const [logs, setLogs] = useState(null);

  useEffect(() => {
    api.get(`/admin/lead-email-logs?lead_id=${leadId}`).then(r => setLogs(r.data)).catch(() => setLogs([]));
  }, [leadId]);

  const resend = async () => {
    try {
      const r = await api.post(`/admin/leads/${leadId}/resend-status-email`);
      if (r.data.success) toast.success("Email re-sent");
      else if (r.data.skipped) toast.warning(`Skipped: ${r.data.skip_reason}`);
      else toast.error(`Failed: ${r.data.error || "unknown"}`);
      const refreshed = await api.get(`/admin/lead-email-logs?lead_id=${leadId}`);
      setLogs(refreshed.data);
    } catch { toast.error("Resend failed"); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur flex items-center justify-center p-4" onClick={onClose} data-testid="lead-email-history-modal">
      <div className="bg-[#0a0a0a] border border-white/15 max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-white/10 flex items-start justify-between">
          <div>
            <div className="eyebrow">Email History</div>
            <h3 className="font-serif text-2xl mt-1">{leadName}</h3>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white text-2xl leading-none">&times;</button>
        </div>
        <div className="p-5 flex-1 overflow-y-auto">
          {logs == null ? (
            <div className="text-white/40 text-center py-10">Loading...</div>
          ) : logs.length === 0 ? (
            <div className="text-white/40 text-center py-10">No status emails sent yet. Change this lead's status to trigger one.</div>
          ) : (
            <div className="space-y-3">
              {logs.map(l => (
                <div key={l.id} className={`bg-black border ${l.success ? "border-green-500/20" : l.skipped ? "border-yellow-500/20" : "border-red-500/30"} p-4`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-white/40">
                        {l.from_status || "—"} <span className="text-[#D4AF37]">→</span> <span className="text-[#D4AF37]">{l.to_status}</span>
                      </div>
                      <div className="text-sm text-white mt-1 truncate">{l.subject || "(no subject)"}</div>
                      <div className="text-[0.65rem] text-white/40 mt-1">{new Date(l.created_at).toLocaleString()}</div>
                    </div>
                    <span className={`text-[0.55rem] tracking-widest uppercase px-2 py-1 ${
                      l.success ? "bg-green-500/20 text-green-300" :
                      l.skipped ? "bg-yellow-500/20 text-yellow-300" :
                      "bg-red-500/20 text-red-300"
                    }`}>
                      {l.success ? "Sent" : l.skipped ? "Skipped" : "Failed"}
                    </span>
                  </div>
                  {(l.error || l.skip_reason) && (
                    <div className="text-xs text-white/50 mt-2">{l.error || l.skip_reason}</div>
                  )}
                  {l.body && (
                    <details className="mt-3">
                      <summary className="text-xs text-[#D4AF37] cursor-pointer">View message body</summary>
                      <pre className="text-xs text-white/70 mt-2 whitespace-pre-wrap font-sans">{l.body}</pre>
                    </details>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="p-4 border-t border-white/10 flex items-center justify-between">
          <div className="text-xs text-white/40">{logs?.length || 0} log{logs?.length === 1 ? "" : "s"}</div>
          <button onClick={resend} className="btn-ghost text-xs" data-testid="resend-status-email">
            <Send size={12}/> Resend Current Status Email
          </button>
        </div>
      </div>
    </div>
  );
}
